import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const DEPARTEMENTS = [
  { id: 22, label: 'Administration & RH',       icon: '👥', color: '#42a5f5', route: '/dashboard-rh' },
  { id: 18, label: 'Location & Immobilier',     icon: '🏠', color: '#1565c0', route: '/dashboard-location' },
  { id: 17, label: 'Marketing & Communication', icon: '📣', color: '#C9A84C', route: '/dashboard-marketing' },
  { id: 19, label: 'Gestion des Projets',       icon: '📋', color: '#ff9800', route: '/dashboard-projets' },
  { id: 16, label: 'Technologie',               icon: '💻', color: '#66bb6a', route: '/dashboard-technologie' },
  { id: null, label: 'Comptabilité & Finance',  icon: '💰', color: '#ab47bc', route: '/dashboard-comptabilite' },
  { id: 24, label: 'Secrétariat - Caisse',      icon: '💵', color: '#C9A84C', route: '/dashboard-caisse' },
];

function KpiBox({ value, label, color }) {
  return (
    <div style={{ textAlign: 'center', flex: 1 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: color || '#fff', fontFamily: 'Cormorant Garamond, serif' }}>
        {value ?? '—'}
      </div>
      <div style={{ fontSize: 9, color: '#556', marginTop: 2 }}>{label}</div>
    </div>
  );
}

function DeptCard({ dept, navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const filters = dept.id ? { departement_id: dept.id } : {};
      const [empRes, tasksRes, projRes] = await Promise.allSettled([
        api.employees.getAll(filters),
        api.tasks.getAll(filters),
        api.projects.getAll(filters),
      ]);

      const employes  = empRes.status   === 'fulfilled' ? (empRes.value?.employees  || []) : [];
      const tasks     = tasksRes.status === 'fulfilled' ? (tasksRes.value?.tasks    || []) : [];
      const projets   = projRes.status  === 'fulfilled' ? (projRes.value?.projects  || []) : [];

      const presents   = employes.filter(e => !!e.present).length;
      const pending    = tasks.filter(t => !t.done).length;
      const enCours    = projets.filter(p => ['en_cours', 'planification', 'production'].includes(p.status)).length;
      const scoreAvg   = employes.length > 0
        ? (employes.reduce((s, e) => s + (e.score_performance || 0), 0) / employes.length).toFixed(1)
        : '—';
      const alertes    = employes.filter(e => !e.present || (e.score_performance || 0) < 5).length;

      setData({ total: employes.length, presents, pending, enCours, scoreAvg, alertes });
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dept.id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{
      background: '#111f30',
      border: `1px solid ${dept.color}33`,
      borderRadius: 14,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        background: `${dept.color}12`,
        borderBottom: `1px solid ${dept.color}22`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>{dept.icon}</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>{dept.label}</div>
            {!loading && data && (
              <div style={{ fontSize: 9, color: '#667', marginTop: 1 }}>
                {data.total} collaborateur{data.total > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {!loading && data?.alertes > 0 && (
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 99,
              background: 'rgba(239,83,80,.15)', color: '#ef8080',
            }}>
              ⚠️ {data.alertes}
            </span>
          )}
          <button
            onClick={() => navigate(dept.route)}
            style={{
              background: `${dept.color}20`,
              border: `1px solid ${dept.color}44`,
              borderRadius: 6,
              padding: '4px 10px',
              color: dept.color,
              fontSize: 10,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Voir →
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ padding: '14px 16px', display: 'flex', gap: 4, flex: 1 }}>
        {loading ? (
          <div style={{ fontSize: 11, color: '#445', fontStyle: 'italic', margin: 'auto' }}>
            Chargement…
          </div>
        ) : !data ? (
          <div style={{ fontSize: 11, color: '#ef8080', margin: 'auto' }}>Erreur de chargement</div>
        ) : (
          <>
            <KpiBox value={data.presents}  label="Présents"     color="#66bb6a" />
            <div style={{ width: 1, background: 'rgba(255,255,255,.05)' }} />
            <KpiBox value={data.pending}   label="Tâches en att." color="#ff9800" />
            <div style={{ width: 1, background: 'rgba(255,255,255,.05)' }} />
            <KpiBox value={data.enCours}   label="Projets actifs" color={dept.color} />
            <div style={{ width: 1, background: 'rgba(255,255,255,.05)' }} />
            <KpiBox value={data.scoreAvg}  label="Score moy."    color="#C9A84C" />
          </>
        )}
      </div>

      {/* Barre présence */}
      {!loading && data && data.total > 0 && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ height: 3, background: 'rgba(255,255,255,.06)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${Math.round(data.presents / data.total * 100)}%`,
              background: dept.color,
              borderRadius: 99,
              transition: 'width .4s',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
            <span style={{ fontSize: 9, color: '#445' }}>Présence</span>
            <span style={{ fontSize: 9, color: dept.color, fontWeight: 600 }}>
              {Math.round(data.presents / data.total * 100)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DeptOverviewSection() {
  const navigate = useNavigate();
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [key, setKey] = useState(0);

  const refresh = () => { setKey(k => k + 1); setLastUpdate(new Date()); };

  return (
    <div>
      {/* Header section */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>🏛️ Vue Départements — Suivi en direct</div>
          <div style={{ fontSize: 11, color: '#667', marginTop: 3 }}>
            Mise à jour : {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            {' · '}{DEPARTEMENTS.length} départements surveillés
          </div>
        </div>
        <button
          onClick={refresh}
          style={{
            background: 'rgba(201,168,76,.1)',
            border: '1px solid rgba(201,168,76,.3)',
            borderRadius: 8,
            padding: '6px 14px',
            color: '#C9A84C',
            fontSize: 11,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          ↻ Actualiser
        </button>
      </div>

      {/* Grille départements */}
      <div key={key} style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: 14,
      }}>
        {DEPARTEMENTS.map(dept => (
          <DeptCard key={dept.route} dept={dept} navigate={navigate} />
        ))}
      </div>
    </div>
  );
}
